'use strict';

const dates = require('util/dates');
const getCookie = require('util/getCookie');

const groupBy = require('lodash/groupBy');
const map = require('lodash/map');
const minBy = require('lodash/minBy');
const maxBy = require('lodash/maxBy');
const uniqWith = require('lodash/uniqWith');
const orderBy = require('lodash/orderBy');
const find = require('lodash/find');
const uniq = require('lodash/uniq');
const min = require('lodash/min');
const max = require('lodash/max');
const forEach = require('lodash/forEach');

// We'll be using really crude caching here. We could use memoize but why get a fat function
// when you can just do the same in vanilla JS with a few lines
const cache = {};

module.exports = ['$http', '$q', 'appConfig', 'locale', ($http, $q, appConfig, locale) => {

    // Process the responses to return a single feed with all MOS OnSale Dates
    function loadPerformances() {
        let currentMOS = getCurrentMOS();
        if (!find(appConfig.modes_of_sale, {mos: currentMOS})) {
            // If the current MOS isn't in the list to be checked then add it
            appConfig.modes_of_sale.unshift({
                mos: currentMOS,
                description: {}
            });
        }

        // Order so WNO are always first
        if (appConfig.show_wno_first) {
            appConfig.modes_of_sale = orderBy(appConfig.modes_of_sale, 'wno', 'desc');
        }

        return loadAllPerformances().then(responses => {
            let dataForCurrentMOS = find(responses, result => {
                return result.mos === currentMOS;
            });

            if (!dataForCurrentMOS) {
                // Fallback to use Public MOS
                currentMOS = appConfig.public_mos;
                dataForCurrentMOS = find(responses, result => {
                    return result.mos === currentMOS;
                });
            }

            dataForCurrentMOS = dataForCurrentMOS.result;

            forEach(dataForCurrentMOS, perf => {
                perf.on_sale_dates = [
                    {
                        mos: currentMOS,
                        on_sale_date: perf.on_sale_date,
                        on_sale_date_moment: dates.moment(perf.on_sale_date),
                    }
                ];

                forEach(responses, feedData => {
                    if (feedData.mos === currentMOS) {
                        return;
                    }
                    let mosPerf = find(feedData.result, mosPerf => {
                        return mosPerf.perf_no === perf.perf_no;
                    });
                    if (mosPerf) {
                        perf.on_sale_dates.push({
                            mos: feedData.mos,
                            on_sale_date: mosPerf.on_sale_date,
                            on_sale_date_moment: dates.moment(mosPerf.on_sale_date),
                        });
                    }
                });
            });

            return dataForCurrentMOS;
        });
    }

    // Load performances for ALL Modes of Sale from endpoint
    function loadAllPerformances() {
        if ('loadPerformances' in cache) {
            return cache.loadAllPerformances;
        }

        const promises = map(appConfig.modes_of_sale, data => {
            return $http
                .get(appConfig.eventFeedURL + '/' + data.mos, { cache: true, withCredentials: true })
                .then(filterOldEvents)
                .then(fixLinkLocales)
                .then(result => ({
                    result: result,
                    mos: data.mos,
                }));
        });

        cache.loadAllPerformances = $q.all(promises);

        return cache.loadAllPerformances;
    }

    function filterOldEvents(response) {
        const hour = dates.moment().startOf('hour');

        return response.data.map(performance => {
            if ('Perf_end_date' in performance.web_contents && performance.web_contents.Perf_end_date) {
                performance.end_date = performance.web_contents.Perf_end_date;
            }

            return performance;
        }).filter(performance => {
            return dates.moment(performance.end_date).isAfter(hour);
        });
    }

    function fixLinkLocales(performances) {
        if (locale !== 'cy') {
            return performances;
        }

        return performances.map(performance => {
            if ('book_url' in performance) {
                performance.book_url = performance.book_url.replace('/en/', '/cy/');
            }

            return performance;
        });
    }

    // Get performances is just an alias
    function getPerformances() {
        if ('getPerformances' in cache) {
            return cache.getPerformances;
        }

        cache.getPerformances = loadPerformances().then(response => {
            // Do some management
            let performances = map(response, performance => {
                // Momentise some dates for ease of use after
                performance.start_date_moment = dates.moment(performance.start_date);
                performance.end_date_moment = dates.moment(performance.end_date);
                performance.on_sale_date_moment = dates.moment(performance.on_sale_date);
                performance.off_sale_date_moment = dates.moment(performance.off_sale_date);

                // Grab where price is present
                performance.pricing = performance.pricing || [];
                let pricing = performance.pricing.filter(item => parseFloat(item.price));

                // We only need unique price values
                pricing = uniqWith(pricing, (a, b) => {
                    return parseFloat(a.price) === parseFloat(b.price);
                });

                // Now, just grab and assign the price values
                performance.pricing = map(pricing, item => parseFloat(item.price));

                // Grab min & max pricing
                performance.min_price = min(performance.pricing) || 0;
                performance.max_price = max(performance.pricing) || 0;

                // Return the updated item
                return performance;
            });

            // Sort by start date & return
            return orderBy(performances, item => item.start_date_moment.valueOf());
        });

        return cache.getPerformances;
    };

    // Get events (basically performances grouped)
    function getEvents() {
        if ('getEvents' in cache) {
            return cache.getEvents;
        }

        cache.getEvents = getPerformances().then(response => {
            // Group by prod season number
            let events = groupBy(response, 'prod_season_no');

            // Run through group and generate a events
            events = map(events, performances => {
                let performance = performances[0];

                // Grab the first performance by start date
                const firstPerformance = minBy(performances, 'start_date');

                // Grab the last performance by end date
                const lastPerformance = maxBy(performances, 'end_date');

                // Grab the last performance by on sale date
                const onSalePerformance = minBy(performances, 'on_sale_date');

                // Grab the last performance by off sale date
                const offSalePerformance = maxBy(performances, 'off_sale_date');

                // Grab facets from performances
                let facets = map(performances, item => item.facets);

                // Merge the facets
                facets = [].concat.apply([], facets);

                // Make then unique
                facets = uniqWith(facets, (a, b) => {
                    return (
                        a.category === b.category &&
                        a.keyword === b.keyword
                    );
                });

                facets = facets.reduce((carry, facet) => {
                    let keywords = facet.keyword.split(' / ');
                    let keyword = facet.keyword;
                    if (keywords.length > 1) {
                        keyword = locale === 'en' ? keywords[0] : keywords[1];
                    }
                    carry.push({
                        keyword: keyword.trim(),
                        category: facet.category,
                    });
                    return carry;
                }, []);

                // Grab pricing from performances
                let pricing = map(performances, item => item.pricing);

                // Merge the pricing
                pricing = [].concat.apply([], pricing);

                // Make then unique
                pricing = uniq(pricing);

                // Grab min & max prices
                const minPrice = min(pricing);
                const maxPrice = max(pricing);

                // Sum the availability
                const availability = performances.reduce((carry, item) => {
                    return carry += parseInt(item.availability, 10);
                }, 0);

                // Return a new event item
                return {
                    // These all come from a performance. Doesn't matter which one.
                    title: performance.title,
                    prod_season_no: performance.prod_season_no,
                    book_url: performance.event_book_url,
                    event_url: performance.event_url,
                    prefix: performance.prefix,
                    suffix: performance.suffix,

                    custom_book_url: performance.custom_book_url,
                    custom_booking_message: performance.custom_booking_message,
                    custom_booking_status: performance.custom_booking_status,

                    image_url: performance.image_url,

                    // Assign the performances
                    performances: performances,

                    // Assign the facets
                    facets: facets,

                    // Assign the pricing
                    pricing: pricing,
                    min_price: minPrice,
                    max_price: maxPrice,

                    // Assign the availability
                    availability: availability,

                    // Store the dates as is...
                    start_date: firstPerformance.start_date,
                    end_date: lastPerformance.end_date,
                    on_sale_date: onSalePerformance.on_sale_date,
                    off_sale_date: offSalePerformance.off_sale_date,

                    date_description: onSalePerformance.date_description,
                    price_description: onSalePerformance.price_description,

                    // ...and as moment objects
                    start_date_moment: firstPerformance.start_date_moment,
                    end_date_moment: lastPerformance.end_date_moment,
                    on_sale_date_moment: onSalePerformance.on_sale_date_moment,
                    off_sale_date_moment: offSalePerformance.off_sale_date_moment,

                    on_sale_dates: getOnSaleDatesForPerforamnces(performances),
                    show_in_listing: performance.show_in_listing,
                    show_in_filter: performance.show_in_filter,

                    status_description: performance.status_description,

                    date_class: 'performance-' + performance.start_date_moment.format('YYYY-MM-DD'),
                };
            });

            // Sort by start date & return
            return orderBy(events, item => item.start_date_moment.valueOf());
        });

        return cache.getEvents;
    }

    function getFoodAndDrinkPerformances(prodSeasonNo, facilityNo) {
        let params = {
            prodSeasonNo: prodSeasonNo,
            facilityNo: facilityNo,
        };

        params = map(params, (value, key) => {
            return encodeURIComponent(key) + '=' + encodeURIComponent(value);
        });

        params = params.join('&');

        return $http.get(appConfig.restaurantFeedURL + '?' + params);
    }

    function foodAndDrinkCheckout(checkoutData) {
        return $http.post(appConfig.restaurantActionURL, checkoutData, {
            withCredentials: true
        });
    }

    function getCurrentMOS() {
        const mos = getCookie(document.cookie, '_current_mos');
        return parseInt(mos ? mos : appConfig.public_mos, 10);
    }

    /**
     * Returns an array of objects with keys of `on_sale_date`, `on_sale_date_moment` and `mos`
     */
    function getOnSaleDatesForPerforamnces(performances) {
        return map(appConfig.modes_of_sale, mosData => {
            const earliestOnSalePerformance = find(performances, performance => {
                const mosOnSaleDate = find(performance.on_sale_dates, date => {
                    return parseInt(date.mos, 10) === parseInt(mosData.mos, 10);
                });

                if (mosOnSaleDate) {
                    return mosOnSaleDate.on_sale_date_moment;
                }

                return performance.on_sale_date_moment;
            });

            const onSaleDates = earliestOnSalePerformance.on_sale_dates;

            let mosOnSale = find(onSaleDates, date => {
                return parseInt(date.mos, 10) === parseInt(mosData.mos, 10);
            });

            if (!mosOnSale) {
                mosOnSale = onSaleDates[0];
            }

            return {
                mos: mosData.mos,
                on_sale_date: mosOnSale.on_sale_date,
                on_sale_date_moment: mosOnSale.on_sale_date_moment,
                formatted_date: mosOnSale.on_sale_date_moment.format('D MMM'),
                label: mosData.description[locale],
            };
        });
    }

    return {
        getPerformances: getPerformances,
        getEvents: getEvents,
        getFoodAndDrinkPerformances: getFoodAndDrinkPerformances,
        foodAndDrinkCheckout: foodAndDrinkCheckout
    }
}];



// WEBPACK FOOTER //
// ./widgets/services/inventoryService.js